/**
 * 형제 서식 제목에서 **서로 겹치는 앞머리**를 덜어 구별되는 꼬리만 남긴다.
 *
 * `2026학년도 2학기 SW융합 멘토링 신청서 (멘토용)` · `… (멘티용)` → `멘토용` · `멘티용`.
 * 프로그램 이름으로 자르지 않는 이유: 같은 것을 가리켜도 표기가 다르다(`26년` vs `2026학년도`).
 * 그래서 제목들끼리 비교한다 - 겹치는 앞머리는 정의상 구별에 쓸모가 없다.
 *
 * 규칙(공개 모집 카드에서 옮겨 왔다, 2026-09-21):
 *  - **어절 경계**에서만 자른다. 적어도 한 어절은 남긴다.
 *  - **덜어낸 뒤에도 서로 달라야 덜어낸다.** 못 가르면 `null` - 부르는 쪽이 원래 제목을 쓴다.
 *  - 남은 것이 통째로 괄호 안이면 괄호를 벗긴다(`(멘토용)` → `멘토용`).
 *  - 제목이 둘 미만이면 비교할 상대가 없으므로 `null`.
 *
 * 화면 점검 기준 A(겹치는 말) - `docs/development/화면-점검-기준.md`.
 */
export function distinctTitles(titles: string[]): string[] | null {
  if (titles.length < 2) return null;
  const tokenized = titles.map((title) => title.trim().split(/\s+/));
  const shortest = Math.min(...tokenized.map((tokens) => tokens.length));

  let shared = 0;
  while (shared < shortest - 1
      && tokenized.every((tokens) => tokens[shared] === tokenized[0][shared])) {
    shared += 1;
  }
  if (shared === 0) return null;

  const stripped = tokenized.map((tokens) => unwrap(
    tokens.slice(shared).join(' ').replace(/^[-–—:·]\s*/, '').trim()));
  const distinct = stripped.every(Boolean) && new Set(stripped).size === stripped.length;
  return distinct ? stripped : null;
}

/** 제목 → 화면에 쓸 짧은 이름. 못 가르면 원래 제목이다. */
export function distinctTitleMap(titles: string[]): Map<string, string> {
  const unique = Array.from(new Set(titles));
  const labels = distinctTitles(unique) ?? unique;
  return new Map(unique.map((title, index) => [title, labels[index]]));
}

/** `(멘토용)` → `멘토용`. 통째로 감싼 한 쌍일 때만 벗긴다. */
function unwrap(text: string): string {
  const inner = /^[(（]([^()（）]+)[)）]$/.exec(text);
  return inner ? inner[1].trim() : text;
}
